import { useEffect, useState, useCallback, useMemo } from 'react';
import { useOidcContext } from '../contexts/OidcContext';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BoltIcon from '@mui/icons-material/Bolt';
import BuildIcon from '@mui/icons-material/Build';
import Tooltip from '@mui/material/Tooltip';
import AccessStatusChip from '../components/AccessStatusChip';
import ListSortFilterBar from '../components/ListSortFilterBar';
import WorkflowBadges from '../components/WorkflowBadges';
import AiGenerateButton from '../components/ai/AiGenerateButton';
import { api } from '../api';
import { HubSeatOut, PortalWorkflowOut } from '../types/apiRequests';
import { useTranslation } from '../i18n/useTranslation';
import { useBeheerder } from '../hooks/useBeheerder';
import { useOrgInfo } from '../contexts/OrgInfoContext';

type SortMode = 'activated' | 'name_asc' | 'name_desc' | 'newest' | 'oldest';
const SORT_KEY = 'sa_portal_wf_sort';
const FILTER_KEY = 'sa_portal_wf_trigger_filter';

// Activated/linked workflows surface first, then ones the org can still add.
const ACCESS_ORDER: Record<string, number> = {
  active: 0,
  instant_available: 1,
  request_pending: 2,
  not_linked: 3,
};

// Stable display order for the trigger filter (webhook is intentionally absent).
const TRIGGER_ORDER = ['manual', 'cron', 'mcp'] as const;
type TriggerType = (typeof TRIGGER_ORDER)[number];
const TRIGGER_LABEL_KEYS = {
  manual: 'workflow.trigger.manual',
  cron: 'workflow.trigger.cron',
  mcp: 'workflow.trigger.mcp',
} as const;

interface ServerConfigState {
  server_id: string;
  server_name: string;
  seat_id: string;
  environment_id: string;
  base_url: string;
  environments: Array<{ id: string; name: string; display_name: string; default_base_url: string }>;
  has_default_base_url: boolean;
}

export default function WorkflowsPage() {
  const { getAccessTokenSilently } = useOidcContext();
  const navigate = useNavigate();
  const t = useTranslation();
  const isBeheerder = useBeheerder();
  const orgInfo = useOrgInfo();
  const isFreeTier = orgInfo?.hub_tier === 'free';
  const [workflows, setWorkflows] = useState<PortalWorkflowOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [requestDialogWorkflow, setRequestDialogWorkflow] = useState<PortalWorkflowOut | null>(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [requestFiles, setRequestFiles] = useState<Record<string, File | null>>({});
  const [serverConfigs, setServerConfigs] = useState<ServerConfigState[]>([]);
  const [seats, setSeats] = useState<HubSeatOut[]>([]);
  const [requestVariableValues, setRequestVariableValues] = useState<Record<string, string>>({});
  const [sortMode, setSortMode] = useState<SortMode>(() => {
    const stored = localStorage.getItem(SORT_KEY);
    const validModes: SortMode[] = ['activated', 'name_asc', 'name_desc', 'newest', 'oldest'];
    return stored && validModes.includes(stored as SortMode) ? (stored as SortMode) : 'activated';
  });
  const [hiddenTriggers, setHiddenTriggers] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(FILTER_KEY) || '[]'); }
    catch { return []; }
  });

  const loadWorkflows = useCallback(async () => {
    try {
      setError(null);
      const token = await getAccessTokenSilently();
      const data = await api.getWorkflows(token);
      setWorkflows(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('workflow.loadError'));
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, t]);

  useEffect(() => {
    void loadWorkflows();
  }, [loadWorkflows]);

  const openRequestDialog = async (workflow: PortalWorkflowOut) => {
    setRequestDialogWorkflow(workflow);
    setRequestMessage('');
    setRequestFiles({});
    const initialValues: Record<string, string> = {};
    for (const def of workflow.variable_definitions || []) {
      const name = (def as { name?: string }).name;
      if (name) initialValues[name] = '';
    }
    setRequestVariableValues(initialValues);
    setServerConfigs(
      (workflow.required_servers || []).map((s) => {
        const envs = (s.environments || []).map((e) => ({
          id: e.id ?? '',
          name: e.name ?? '',
          display_name: e.display_name ?? '',
          default_base_url: e.default_base_url ?? '',
        }));
        const autoEnv = envs.length === 1 ? envs[0] : null;
        return {
          server_id: s.server_id ?? '',
          server_name: s.server_display_name ?? s.server_name ?? '',
          seat_id: '',
          environment_id: autoEnv?.id ?? '',
          base_url: autoEnv?.default_base_url ?? '',
          environments: envs,
          has_default_base_url: s.has_default_base_url ?? false,
        };
      }),
    );
    try {
      const token = await getAccessTokenSilently();
      const seatData = await api.getSeats(token);
      setSeats(seatData);
    } catch {
      setSeats([]);
    }
  };

  const handleSubmitRequest = async () => {
    if (!requestDialogWorkflow) return;
    const required = requestDialogWorkflow.required_attachments ?? [];
    const filledRoles = required.filter((slot) => requestFiles[slot.role!]);
    if (required.length > 0 && filledRoles.length === 0) {
      setError(t('workflow.requestDialog.attachmentRequired'));
      return;
    }
    try {
      setActionInProgress(requestDialogWorkflow.name);
      const token = await getAccessTokenSilently();
      // /request-access takes a single (role, file) pair; extra roles go via the admin link-attachments endpoint after approval.
      const primary = filledRoles[0];
      // Only send variables the beheerder actually filled in — empty strings
      // would clobber any value the workflow author might set as a default
      // later.
      const filledVariables: Record<string, string> = {};
      for (const [name, value] of Object.entries(requestVariableValues)) {
        if (value !== '') filledVariables[name] = value;
      }
      await api.requestWorkflowAccess(
        token,
        requestDialogWorkflow.name,
        {
          variable_values: filledVariables,
          server_configs: serverConfigs.map((sc) => ({
            server_id: sc.server_id,
            seat_id: sc.seat_id || undefined,
            environment_id: sc.environment_id || undefined,
            base_url: sc.base_url || undefined,
          })),
          message: requestMessage || undefined,
        },
        primary ? requestFiles[primary.role!] : null,
        primary?.role,
      );
      setRequestDialogWorkflow(null);
      setRequestFiles({});
      await loadWorkflows();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('workflow.actionError'));
    } finally {
      setActionInProgress(null);
    }
  };

  const handleInstantAdd = async (workflow: PortalWorkflowOut) => {
    try {
      setActionInProgress(workflow.name);
      const token = await getAccessTokenSilently();
      await api.instantAddWorkflow(token, workflow.name);
      await loadWorkflows();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('workflow.actionError'));
    } finally {
      setActionInProgress(null);
    }
  };

  const updateServerConfig = (index: number, field: keyof ServerConfigState, value: string) => {
    setServerConfigs((prev) =>
      prev.map((sc, i) => {
        if (i !== index) return sc;
        const updated = { ...sc, [field]: value };
        if (field === 'environment_id') {
          const env = sc.environments.find((e) => e.id === value);
          if (env?.default_base_url) {
            updated.base_url = env.default_base_url;
          }
        }
        return updated;
      }),
    );
  };

  const handleSortChange = (mode: SortMode) => {
    setSortMode(mode);
    localStorage.setItem(SORT_KEY, mode);
  };

  const handleToggleTrigger = (trigger: string) => {
    const next = hiddenTriggers.includes(trigger)
      ? hiddenTriggers.filter((tt) => tt !== trigger)
      : [...hiddenTriggers, trigger];
    setHiddenTriggers(next);
    localStorage.setItem(FILTER_KEY, JSON.stringify(next));
  };

  // Non-beheerders can't activate workflows, so "activated first" is meaningless
  // for them — fall back to alphabetical (mirrors the servers list).
  const effectiveSort: SortMode = (!isBeheerder && sortMode === 'activated') ? 'name_asc' : sortMode;

  const triggerTypes = useMemo(() => {
    const present = new Set(
      workflows.map((w) => w.trigger_type).filter((tt): tt is string => !!tt),
    );
    return TRIGGER_ORDER.filter((tt) => present.has(tt));
  }, [workflows]);

  const sortedWorkflows = useMemo(() => {
    const filtered = hiddenTriggers.length > 0
      ? workflows.filter((w) => !hiddenTriggers.includes(w.trigger_type || ''))
      : workflows;

    return [...filtered].sort((a, b) => {
      if (effectiveSort === 'activated') {
        const orderA = ACCESS_ORDER[a.access_status] ?? 9;
        const orderB = ACCESS_ORDER[b.access_status] ?? 9;
        if (orderA !== orderB) return orderA - orderB;
        return a.display_name.localeCompare(b.display_name);
      }
      if (effectiveSort === 'name_asc') return a.display_name.localeCompare(b.display_name);
      if (effectiveSort === 'name_desc') return b.display_name.localeCompare(a.display_name);
      if (effectiveSort === 'newest') return (b.created || '').localeCompare(a.created || '');
      if (effectiveSort === 'oldest') return (a.created || '').localeCompare(b.created || '');
      return 0;
    });
  }, [workflows, effectiveSort, hiddenTriggers]);

  const sortOptions = [
    ...(isBeheerder ? [{ value: 'activated', label: t('servers.sort.activated') }] : []),
    { value: 'name_asc', label: t('servers.sort.nameAsc') },
    { value: 'name_desc', label: t('servers.sort.nameDesc') },
    { value: 'newest', label: t('servers.sort.newest') },
    { value: 'oldest', label: t('servers.sort.oldest') },
  ];

  const visibleTriggers = triggerTypes.filter((tt) => !hiddenTriggers.includes(tt));
  const triggerFilterLabel = visibleTriggers.length === triggerTypes.length
    ? t('workflow.filter.allTriggers')
    : visibleTriggers.length === 1
      ? t(TRIGGER_LABEL_KEYS[visibleTriggers[0]])
      : `${visibleTriggers.length} ${t('servers.filter.selected')}`;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <AccountTreeIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('workflow.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('workflow.subtitle')}
          </Typography>
        </Box>
        {isBeheerder && <AiGenerateButton />}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && workflows.length > 0 && (
        <ListSortFilterBar
          sortValue={effectiveSort}
          sortOptions={sortOptions}
          onSortChange={(v) => handleSortChange(v as SortMode)}
          filter={
            triggerTypes.length > 1
              ? {
                  label: triggerFilterLabel,
                  options: triggerTypes.map((tt) => ({
                    value: tt,
                    label: t(TRIGGER_LABEL_KEYS[tt]),
                    checked: !hiddenTriggers.includes(tt),
                  })),
                  onToggle: handleToggleTrigger,
                }
              : undefined
          }
        />
      )}

      <Grid container spacing={2}>
        {loading
          ? Array.from({ length: 3 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
            </Grid>
          ))
          : sortedWorkflows.map((workflow) => {
            const isActioning = actionInProgress === workflow.name;

            return (
              <Grid item xs={12} sm={6} md={4} key={workflow.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'visible',
                  }}
                >
                  <CardActionArea
                    onClick={() => navigate(`/workflows/${workflow.name}`)}
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      p: 0,
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3, '&:last-child': { pb: 3 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            bgcolor: workflow.access_status === 'active' ? 'primary.main' : 'action.hover',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <AccountTreeIcon
                            sx={{
                              fontSize: 20,
                              color: workflow.access_status === 'active' ? 'primary.contrastText' : 'text.secondary',
                            }}
                          />
                        </Box>
                        <AccessStatusChip status={workflow.access_status} />
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '1rem',
                          letterSpacing: '-0.01em',
                          mb: 0.25,
                          lineHeight: 1.3,
                        }}
                        noWrap
                      >
                        {workflow.display_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          mb: 2.5,
                          flexGrow: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontSize: '0.83rem',
                        }}
                      >
                        {workflow.description || t('common.noDescription')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: 'text.disabled',
                            fontWeight: 500,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {(workflow.required_servers || []).length} servers
                        </Typography>
                        <WorkflowBadges isMcp={workflow.is_mcp} hasAi={workflow.has_ai} />
                      </Box>
                    </CardContent>
                  </CardActionArea>

                  {workflow.access_status === 'instant_available' && isBeheerder && (
                    <Box sx={{ px: 3, pb: 2.5, pt: 0 }}>
                      <Tooltip
                        title={isFreeTier ? t('workflow.upgradeRequired') : ''}
                        disableHoverListener={!isFreeTier}
                      >
                        <span style={{ display: 'block' }}>
                          <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            disabled={isActioning || isFreeTier}
                            startIcon={<BoltIcon sx={{ fontSize: '16px !important' }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleInstantAdd(workflow);
                            }}
                            sx={{
                              fontSize: '0.8rem',
                              py: 0.75,
                            }}
                          >
                            {t('workflow.instantAdd')}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  )}

                  {workflow.access_status === 'not_linked' && isBeheerder && (
                    <Box sx={{ px: 3, pb: 2.5, pt: 0 }}>
                      <Tooltip
                        title={isFreeTier ? t('workflow.upgradeRequired') : ''}
                        disableHoverListener={!isFreeTier}
                      >
                        <span style={{ display: 'block' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            disabled={isActioning || isFreeTier}
                            endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              void openRequestDialog(workflow);
                            }}
                            sx={{
                              borderColor: 'rgba(0,0,0,0.12)',
                              color: 'primary.main',
                              fontSize: '0.8rem',
                              py: 0.75,
                              '&:hover': {
                                borderColor: 'primary.main',
                                bgcolor: 'rgba(0,0,0,0.02)',
                              },
                            }}
                          >
                            {t('workflow.requestAccess')}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </Card>
              </Grid>
            );
          })}
      </Grid>

      {!loading && !error && workflows.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <AccountTreeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('workflow.empty')}
          </Typography>
        </Box>
      )}
      <Dialog
        open={requestDialogWorkflow !== null}
        onClose={() => setRequestDialogWorkflow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('workflow.requestAccess')}: {requestDialogWorkflow?.display_name}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('workflow.requestDialog.description')}
          </Typography>

          {serverConfigs.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('workflow.requestDialog.mcpConfig')}
              </Typography>
              {serverConfigs.map((sc, index) => (
                <Box
                  key={sc.server_id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BuildIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {sc.server_name}
                    </Typography>
                  </Box>
                  {requestDialogWorkflow?.seat_required && (
                    <TextField
                      select
                      label={t('workflow.requestDialog.seat')}
                      value={sc.seat_id}
                      onChange={(e) => updateServerConfig(index, 'seat_id', e.target.value)}
                      size="small"
                      fullWidth
                      required
                    >
                      {seats.map((seat) => (
                        <MenuItem key={seat.id} value={seat.id}>
                          {seat.auth0_email}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {sc.environments.length > 1 && (
                    <TextField
                      select
                      label={t('workflow.requestDialog.environment')}
                      value={sc.environment_id}
                      onChange={(e) => updateServerConfig(index, 'environment_id', e.target.value)}
                      size="small"
                      fullWidth
                    >
                      {sc.environments.map((env) => (
                        <MenuItem key={env.id} value={env.id}>
                          {env.display_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {!sc.has_default_base_url && (
                    <TextField
                      label={t('workflow.requestDialog.baseUrl')}
                      value={sc.base_url}
                      onChange={(e) => updateServerConfig(index, 'base_url', e.target.value)}
                      size="small"
                      fullWidth
                      placeholder="https://api.example.com"
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}

          {(requestDialogWorkflow?.variable_definitions ?? []).length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('workflow.requestDialog.variables')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('workflow.requestDialog.variablesHelper')}
                </Typography>
              </Box>
              {(requestDialogWorkflow?.variable_definitions ?? []).map((def) => {
                const name = (def as { name?: string; description?: string }).name;
                if (!name) return null;
                const description = (def as { name?: string; description?: string }).description;
                return (
                  <TextField
                    key={name}
                    label={name}
                    value={requestVariableValues[name] ?? ''}
                    onChange={(e) =>
                      setRequestVariableValues((prev) => ({ ...prev, [name]: e.target.value }))
                    }
                    size="small"
                    fullWidth
                    helperText={description || undefined}
                    InputProps={{
                      sx: { fontFamily: '"SF Mono", "Fira Code", ui-monospace, monospace', fontSize: 13 },
                    }}
                  />
                );
              })}
            </Box>
          )}

          {(requestDialogWorkflow?.required_attachments ?? []).map((slot) => {
            const role = slot.role!;
            const file = requestFiles[role] ?? null;
            return (
              <Box key={role} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {slot.label || role}
                </Typography>
                {slot.description && (
                  <Typography variant="caption" color="text.secondary">
                    {slot.description}
                  </Typography>
                )}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: file ? 'primary.main' : 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Button component="label" variant="outlined" size="small" sx={{ textTransform: 'none' }}>
                    {t('workflow.requestDialog.attachmentChoose')}
                    <input
                      type="file"
                      hidden
                      accept={slot.format_hint ?? undefined}
                      onChange={(e) =>
                        setRequestFiles((prev) => ({
                          ...prev,
                          [role]: e.target.files?.[0] ?? null,
                        }))
                      }
                    />
                  </Button>
                  <Typography variant="body2" color={file ? 'text.primary' : 'text.secondary'}>
                    {file?.name || t('workflow.requestDialog.attachmentNone')}
                  </Typography>
                </Box>
              </Box>
            );
          })}

          <TextField
            label={t('workflow.requestDialog.message')}
            value={requestMessage}
            onChange={(e) => setRequestMessage(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
            placeholder={t('workflow.requestDialog.messagePlaceholder')}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRequestDialogWorkflow(null)} sx={{ textTransform: 'none' }}>
            {t('workflow.requestDialog.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmitRequest()}
            disabled={actionInProgress !== null}
            sx={{ textTransform: 'none' }}
          >
            {t('workflow.requestDialog.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
